import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  CreditCard, 
  TrendingUp, 
  Users, 
  Clock,
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommercialActivityPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/commercial-activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load commercial activity');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionBadge = (tier) => {
    const colors = {
      free: 'bg-gray-100 text-gray-800',
      starter: 'bg-blue-100 text-blue-800',
      professional: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <Badge className={colors[tier] || 'bg-gray-100 text-gray-800'}>
        {tier?.toUpperCase()}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const colors = {
      trial: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return (
      <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Commercial Activity</h1>
          <p className="text-gray-600 mt-2">Track subscriptions, trials, and revenue</p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Subscription Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Free</p>
                <p className="text-3xl font-bold text-gray-600">
                  {data?.subscription_stats?.free || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Starter</p>
                <p className="text-3xl font-bold text-blue-600">
                  {data?.subscription_stats?.starter || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Professional</p>
                <p className="text-3xl font-bold text-purple-600">
                  {data?.subscription_stats?.professional || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Enterprise</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {data?.subscription_stats?.enterprise || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trial Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Trials</p>
                <p className="text-3xl font-bold text-green-600">
                  {data?.trial_stats?.active_trials || 0}
                </p>
                <p className="text-xs text-gray-400 mt-1">Users currently in trial period</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Expired Trials</p>
                <p className="text-3xl font-bold text-red-600">
                  {data?.trial_stats?.expired_trials || 0}
                </p>
                <p className="text-xs text-gray-400 mt-1">Need follow-up for conversion</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription Details</CardTitle>
          <CardDescription>All users with their subscription information</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trial Ends</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.users?.map((user, index) => {
                  const trialEnded = user.trial_ends_at && new Date(user.trial_ends_at) < new Date();
                  // Determine if user has likely paid (not free, not in trial, status is active)
                  const hasPaid = user.subscription_tier !== 'free' && 
                                  user.subscription_status === 'active' && 
                                  !user.subscription_status?.includes('trial');
                  return (
                    <tr key={index} className={`hover:bg-gray-50 ${trialEnded ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {user.email}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {user.company_name}
                      </td>
                      <td className="px-4 py-4">
                        {getSubscriptionBadge(user.subscription_tier)}
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(user.subscription_status)}
                      </td>
                      <td className="px-4 py-4">
                        {hasPaid ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Paid
                          </Badge>
                        ) : user.subscription_status === 'trial' ? (
                          <Badge className="bg-yellow-100 text-yellow-800">
                            <Clock className="w-3 h-3 mr-1" />
                            Trial
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            No
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {user.trial_ends_at ? (
                          <span className={trialEnded ? 'text-red-600 font-medium' : ''}>
                            {new Date(user.trial_ends_at).toLocaleDateString()}
                            {trialEnded && ' (Expired)'}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <a 
                          href={`mailto:${user.email}?subject=Snow Exchange - Your subscription`}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 rounded-md hover:bg-primary/20 transition-colors"
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Contact
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CommercialActivityPage;
